import React from "react";
import { useHistory } from "react-router-dom";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import { FaChevronRight, FaHeartbeat, FaBed } from "react-icons/fa";
import {
  MdOutlineLocalHospital,
  MdOutlineEmergency,
  MdOutlineSevereCold,
} from "react-icons/md";
import { GiScalpel } from "react-icons/gi";

/* ─── Accent / icon colours per module ─── */
const MODULE_THEME = {
  opd: { accent: "#189FAA", iconColor: "#189FAA", iconBg: "#E0F7FA" },
  emergency: { accent: "#EF4444", iconColor: "#EF4444", iconBg: "#FEE2E2" },
  icu: { accent: "#6366F1", iconColor: "#6366F1", iconBg: "#EDE9FE" },
  ot: { accent: "#8B5CF6", iconColor: "#8B5CF6", iconBg: "#F3E8FF" },
  wards: { accent: "#F59E0B", iconColor: "#F59E0B", iconBg: "#FEF3C7" },
};

/* ─── Card definitions ─── */
const CARDS = [
  {
    id: "opd",
    label: "OPD",
    route: APP_ROUTES.DASHBOARD,
    Icon: MdOutlineLocalHospital,
    mainLabel: "Today's Patients",
    stats: [
      { color: "#F59E0B", label: "Pending", valueKey: "opd_pending" },
      { color: "#22C55E", label: "Completed", valueKey: "opd_completed" },
    ],
  },
  {
    id: "emergency",
    label: "Emergency",
    route: APP_ROUTES.EMERGENCY,
    Icon: MdOutlineEmergency,
    mainLabel: "Active Cases",
    stats: [
      { color: "#EF4444", label: "Critical", valueKey: "emergency_critical" },
      { color: "#F59E0B", label: "Urgent", valueKey: "emergency_urgent" },
    ],
  },
  {
    id: "icu",
    label: "ICU",
    route: APP_ROUTES.ICUWARDS,
    Icon: FaHeartbeat,
    mainLabel: "Assigned Patients",
    stats: [
      { color: "#EF4444", label: "Critical", valueKey: "icu_critical" },
      { color: "#22C55E", label: "Stable", valueKey: "icu_stable" },
    ],
  },
  {
    id: "ot",
    label: "OT",
    route: APP_ROUTES.OPERATIONTHEATRE,
    Icon: GiScalpel,
    mainLabel: "Today's Surgeries",
    stats: [
      { color: "#F59E0B", label: "Scheduled", valueKey: "ot_scheduled" },
      { color: "#22C55E", label: "Completed", valueKey: "ot_completed" },
    ],
  },
  {
    id: "wards",
    label: "Wards",
    route: APP_ROUTES.INPATIENTS,
    Icon: FaBed,
    mainLabel: "Inpatients",
    stats: [
      { color: "#EF4444", label: "Critical", valueKey: "wards_critical" },
      { color: "#22C55E", label: "Stable", valueKey: "wards_stable" },
    ],
  },
];

/* ─── Mini stat badge ─── */
const StatBadge = ({ color, label, value }) => (
  <div className="flex items-center gap-1">
    <span
      className="w-2 h-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
    <span className="text-[10px] font-sans font-semibold text-gray-600 leading-none">
      {value}
    </span>
    <span className="text-[10px] font-sans text-gray-400 leading-none">
      {label}
    </span>
  </div>
);

/* ─── Default fallback counts (shown when API doesn't return a value) ─── */
const DEFAULT_COUNTS = {
  opd_pending: 12,
  opd_completed: 38,
  emergency_critical: 4,
  emergency_urgent: 9,
  icu_critical: 6,
  icu_stable: 14,
  ot_scheduled: 5,
  ot_completed: 3,
  wards_critical: 8,
  wards_stable: 27,
};

/* ─── Main component ─── */
function HimsModuleCards({ counts = {} }) {
  const history = useHistory();

  const getVal = (key) => {
    const v = counts?.[key];
    if (v !== undefined && v !== null) return v;
    return DEFAULT_COUNTS[key] ?? 0;
  };

  return (
    <div className="w-full mt-4">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "12px",
        }}
      >
        {CARDS.map((card) => {
          const { accent, iconColor, iconBg } = MODULE_THEME[card.id];
          const IconComp = card.Icon;

          return (
            <div
              key={card.id}
              onClick={() => history.push(card.route)}
              className="relative bg-white border rounded-xl shadow-sm cursor-pointer
                         transition-all duration-200 hover:shadow-md hover:-translate-y-0.5
                         flex flex-col overflow-hidden"
              style={{ paddingBottom: "14px" }}
            >
              {/* Top row: icon + chevron */}
              <div className="flex items-center justify-between px-3 pt-3">
                <div className="flex my-4">
                  {" "}
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: iconBg }}
                  >
                    <IconComp style={{ color: iconColor, fontSize: "20px" }} />
                  </div>
                  <p className="px-3 mt-2 text-sm font-bold font-sans text-[#333333] leading-tight">
                    {card.label}
                  </p>
                </div>

                <FaChevronRight className="text-gray-400 text-xs" />
              </div>

              {/* Module label */}

              {/* Sub-label */}
              <p className="px-3 mt-0.5 text-[10px] font-sans text-gray-400 leading-none">
                {card.mainLabel}
              </p>

              {/* Stat badges */}
              <div className="px-3 mt-2 flex gap-2 flex-wrap">
                {card.stats.map((s) => (
                  <StatBadge
                    key={s.valueKey}
                    color={s.color}
                    label={s.label}
                    value={getVal(s.valueKey)}
                  />
                ))}
              </div>

              {/* Bottom accent bar */}
              <div
                className="absolute bottom-0 left-0 w-full"
                style={{ height: "3px", backgroundColor: accent }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default HimsModuleCards;
